import React, { useState, useEffect, useRef } from 'react';

const THEMES = [
    {
        key: 'system',
        label: 'System',
        icon: (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="14" rx="2"/>
                <path d="M8 21h8M12 17v4"/>
            </svg>
        ),
    },
    {
        key: 'dark',
        label: 'Dark',
        icon: (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z"/>
            </svg>
        ),
    },
    {
        key: 'light',
        label: 'Light',
        icon: (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5"/>
                <line x1="12" y1="1" x2="12" y2="3"/>
                <line x1="12" y1="21" x2="12" y2="23"/>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                <line x1="1" y1="12" x2="3" y2="12"/>
                <line x1="21" y1="12" x2="23" y2="12"/>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
            </svg>
        ),
    },
];

function applyTheme(preference) {
    const root = document.documentElement;
    let resolved = preference;
    if (preference === 'system') {
        resolved = window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
    }
    root.setAttribute('data-theme', resolved);
}

function ThemeToggle() {
    const [current, setCurrent] = useState(() => {
        return localStorage.getItem('sw-theme') || 'system';
    });
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    // Apply on mount + when current changes
    useEffect(() => {
        applyTheme(current);
        localStorage.setItem('sw-theme', current);
    }, [current]);

    // Listen to OS preference changes when in system mode
    useEffect(() => {
        const mq = window.matchMedia('(prefers-color-scheme: light)');
        const handler = () => { if (current === 'system') applyTheme('system'); };
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, [current]);

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const active = THEMES.find(t => t.key === current);

    const select = (key) => {
        setCurrent(key);
        setOpen(false);
    };

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            {/* Trigger button */}
            <button
                onClick={() => setOpen(o => !o)}
                title={`Theme: ${active.label}`}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    padding: '0.45rem 0.85rem',
                    background: 'transparent',
                    border: '1px solid var(--border)',
                    borderRadius: '100px',
                    color: 'var(--dim)',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-mono)',
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    transition: 'border-color 0.2s, color 0.2s',
                    whiteSpace: 'nowrap',
                    ...(open ? { borderColor: 'var(--spark)', color: 'var(--spark)' } : {}),
                }}
                onMouseEnter={e => {
                    e.currentTarget.style.borderColor = 'var(--spark)';
                    e.currentTarget.style.color = 'var(--spark)';
                }}
                onMouseLeave={e => {
                    if (!open) {
                        e.currentTarget.style.borderColor = 'var(--border)';
                        e.currentTarget.style.color = 'var(--dim)';
                    }
                }}
            >
                {active.icon}
                <span>{active.label}</span>
                {/* Chevron */}
                <svg
                    width="10" height="10" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="2.5"
                    strokeLinecap="round"
                    style={{
                        transition: 'transform 0.2s',
                        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                        marginLeft: '1px',
                    }}
                >
                    <polyline points="6 9 12 15 18 9"/>
                </svg>
            </button>

            {/* Dropdown */}
            <div style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: 0,
                background: 'var(--forge)',
                border: '1px solid var(--border)',
                borderTop: '2px solid var(--spark)',
                borderRadius: '0.75rem',
                overflow: 'hidden',
                minWidth: '130px',
                zIndex: 1100,
                opacity: open ? 1 : 0,
                transform: open ? 'translateY(0) scale(1)' : 'translateY(-6px) scale(0.97)',
                pointerEvents: open ? 'auto' : 'none',
                transition: 'opacity 0.2s ease, transform 0.2s ease',
                boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
            }}>
                {THEMES.map(({ key, label, icon }) => {
                    const isActive = current === key;
                    return (
                        <button
                            key={key}
                            onClick={() => select(key)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.6rem',
                                width: '100%',
                                padding: '0.75rem 1rem',
                                background: isActive ? 'rgba(255,107,43,0.08)' : 'transparent',
                                border: 'none',
                                borderBottom: '1px solid var(--border)',
                                color: isActive ? 'var(--spark)' : 'var(--dim)',
                                cursor: 'pointer',
                                fontFamily: 'var(--font-mono)',
                                fontSize: '0.72rem',
                                fontWeight: isActive ? 700 : 500,
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                                textAlign: 'left',
                                transition: 'background 0.15s, color 0.15s',
                            }}
                            onMouseEnter={e => {
                                if (!isActive) {
                                    e.currentTarget.style.background = 'rgba(255,107,43,0.05)';
                                    e.currentTarget.style.color = 'var(--ghost)';
                                }
                            }}
                            onMouseLeave={e => {
                                if (!isActive) {
                                    e.currentTarget.style.background = 'transparent';
                                    e.currentTarget.style.color = 'var(--dim)';
                                }
                            }}
                        >
                            {icon}
                            {label}
                            {isActive && (
                                <span style={{
                                    marginLeft: 'auto',
                                    width: '5px', height: '5px',
                                    borderRadius: '50%',
                                    background: 'var(--spark)',
                                    flexShrink: 0,
                                }} />
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export default ThemeToggle;